// External dependencies
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { VueQueryPlugin } from '@tanstack/vue-query';

// Internal core utilities
import { errorService } from './core/error/errorService';
import { useNotificationStore } from './core/notifications/useNotificationStore';
import { initializeApplicationInsights } from './services/applicationInsights';
import { initializeRaygun } from './services/raygun';
import { validateEnvironment } from './services/environment';
import router from './router/index';
import { useAuthStore } from './domains/auth/stores/useAuthStore';
import axiosInstance from './services/api/axios';
import { authEndpoints } from './services/api/apiConfig';

// App & styles
import App from './App.vue';
import './assets/styles/main.css';

validateEnvironment();

// Initialize Application Insights early (before error service)
const appInsightsInitialized = initializeApplicationInsights();
if (appInsightsInitialized) {
  console.info('[App] Application Insights initialized successfully');
} else {
  console.info('[App] Application Insights not configured or failed to initialize');
}

// Initialize Raygun crash reporting
const raygunInitialized = initializeRaygun();
if (raygunInitialized) {
  console.info('[App] Raygun initialized successfully');
} else {
  console.info('[App] Raygun not configured or failed to initialize');
}

const app = createApp(App);

errorService.init();

// Setup Vue-specific error handler
// Catches errors during component lifecycle (render, setup, watchers, etc.)
// - err: The error that was thrown
// - info: Context where error occurred (e.g., "render", "setup", "watcher")
app.config.errorHandler = (err, _instance, info) => {
  console.error('[Vue Error]', { err, info });
  errorService.handleError(err, `Vue: ${info}`);
};

const pinia = createPinia();
app.use(pinia);
app.use(VueQueryPlugin);

// Inject notification store after Pinia is initialized
// Must be done here to ensure useNotificationStore() works properly
errorService.setNotificationStore(useNotificationStore());

app.use(router);

// Service worker registration is handled by AppUpdater.vue (useRegisterSW with Force Update & Sync workflow)

// Initialize authentication on app startup
// Check if refresh token exists in localStorage and attempt to refresh access token
// Must be done after Pinia is initialized
const initializeAuth = async () => {
  const authStore = useAuthStore();

  if (authStore.actions.loadFromStorage()) {
    try {
      const refreshToken = authStore.actions.getRefreshToken();
      if (refreshToken) {
        const response = await axiosInstance.post<{
          accessToken: string;
          refreshToken: string;
          user: {
            userAccountId: number;
            name: string;
            lastName: string;
            brokerCode?: string;
          };
        }>(authEndpoints.REFRESH(), {
          refreshToken,
        });

        authStore.actions.login({
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          user: response.data.user,
        });

        if (import.meta.env.DEV) {
          console.info('[Auth] Successfully refreshed token on app initialization');
        }
      }
    } catch (error) {
      // Refresh failed - clear invalid token
      authStore.actions.logout();
      if (import.meta.env.DEV) {
        console.info('[Auth] Failed to refresh token on app initialization, cleared tokens', error);
      }
    }
  }
};

(async () => {
  await initializeAuth();
  app.mount('#app');
})();
